
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function BackgroundMusic() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showSlider, setShowSlider] = useState(false);
  const [volume, setVolume] = useState(30); // 0-100 scale
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const oscillatorsRef = useRef<OscillatorNode[]>([]);
  const meowAudioRef = useRef<HTMLAudioElement | null>(null);
  const meowIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const noteIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Show button after 2 seconds
    const timer = setTimeout(() => setIsVisible(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Close slider when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSlider(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update volume when slider changes
  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = volume / 100;
    }
    if (meowAudioRef.current) {
      meowAudioRef.current.volume = (volume / 100) * 0.07;
    }
  }, [volume]);

  const playMeowSound = () => {
    if (!meowAudioRef.current) {
      const meow = new Audio();
      meow.volume = (volume / 100) * 0.07;
      
      const meowSounds = [
        'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBgoOEhYaHiImKi4yNjo+QkZKTlJWWl5iZmpucnZ6foKGio6SlpqeoqaqrrK2ur7CxsrO0tba3uLm6u7y9vr/AwcLDxMXGx8jJysvMzc7P0NHS09TV1tfY2drb3N3e3+Dh4uPk5ebn6Onq6+zt7u/w8fLz9PX29/j5+vv8/f7/AQIDBAUGBwgJCgsMDQ4PEBESExQVFhcYGRobHB0eHyAhIiMkJSYnKCkqKywtLi8wMTIzNDU2Nzg5Ojs8PT4/QEFCQ0RFRkdISUpLTE1OT1BRUlNUVVZXWFlaW1xdXl9gYWJjZGVmZ2hpamtsbW5vcHFyc3R1dnd4eXp7fH1+f4CBgoOEhYaHiImKi4yNjo+QkZKTlJWWl5iZmpucnZ6foKGio6SlpqeoqaqrrK2ur7CxsrO0tba3uLm6u7y9vr/AwcLDxMXGx8jJysvMzc7P0NHS09TV1tfY2drb3N3e3+Dh4uPk5ebn6Onq6+zt7u/w8fLz9PX29/j5+vv8/f7/',
      ];
      
      meow.src = meowSounds[0];
      meowAudioRef.current = meow;
    }
    
    meowAudioRef.current.currentTime = 0;
    meowAudioRef.current.play().catch((error) => {
      console.error('Failed to play meow sound:', error);
    });
  };

  const startMusic = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        gainNodeRef.current = audioContextRef.current.createGain();
        gainNodeRef.current.gain.value = volume / 100;
        gainNodeRef.current.connect(audioContextRef.current.destination);
      }

      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }

      const notes = [261.63, 293.66, 329.63, 349.23, 392.00, 440.00, 493.88, 523.25];
      
      const playNote = () => {
        if (!audioContextRef.current || !gainNodeRef.current) return;
        
        const note = notes[Math.floor(Math.random() * notes.length)];
        const oscillator = audioContextRef.current.createOscillator();
        const noteGain = audioContextRef.current.createGain();
        
        oscillator.type = 'sine';
        oscillator.frequency.value = note;
        
        noteGain.gain.value = 0.05;
        
        oscillator.connect(noteGain);
        noteGain.connect(gainNodeRef.current);
        
        oscillator.start();
        noteGain.gain.exponentialRampToValueAtTime(0.001, audioContextRef.current.currentTime + 2);
        oscillator.stop(audioContextRef.current.currentTime + 2);
        
        oscillatorsRef.current.push(oscillator);
      };

      noteIntervalRef.current = setInterval(() => {
        playNote();
      }, 3000 + Math.random() * 2000);

      playNote();

      const scheduleMeow = () => {
        const delay = 30000 + Math.random() * 60000;
        meowIntervalRef.current = setTimeout(() => {
          playMeowSound();
          scheduleMeow();
        }, delay);
      };

      setTimeout(() => {
        playMeowSound();
        scheduleMeow();
      }, 5000);

    } catch (error) {
      console.error('Audio initialization failed:', error);
    }
  };

  const stopMusic = () => {
    oscillatorsRef.current.forEach(osc => {
      try {
        osc.stop();
      } catch (error) {
        console.error('Failed to stop oscillator:', error);
      }
    });
    oscillatorsRef.current = [];

    if (noteIntervalRef.current) {
      clearInterval(noteIntervalRef.current);
      noteIntervalRef.current = null;
    }

    if (meowIntervalRef.current) {
      clearTimeout(meowIntervalRef.current);
      meowIntervalRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state === 'running') {
      audioContextRef.current.suspend();
    }
  };

  const toggleMusic = () => {
    if (isPlaying) {
      stopMusic();
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      startMusic();
    }
  };

  useEffect(() => {
    return () => {
      stopMusic();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const getVolumeIcon = () => {
    if (volume === 0 || !isPlaying) return 'ri-volume-mute-line';
    if (volume < 33) return 'ri-volume-down-line';
    if (volume < 66) return 'ri-volume-up-line';
    return 'ri-volume-up-fill';
  };

  if (!isVisible) return null;

  return (
    <div ref={containerRef} className="fixed bottom-24 right-6 z-50">
      <AnimatePresence>
        {showSlider && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-full right-0 mb-3 bg-white rounded-2xl shadow-xl border border-gray-200 p-4 w-48"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <span className="font-body text-sm font-semibold text-gray-700">Volume</span>
              <span className="font-body text-xs text-teal-600 font-medium">{volume}%</span>
            </div>

            {/* Slider */}
            <div className="relative">
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-teal-500"
                style={{
                  background: `linear-gradient(to right, #14b8a6 0%, #14b8a6 ${volume}%, #e5e7eb ${volume}%, #e5e7eb 100%)`
                }}
              />
            </div>

            {/* Quick Volume Buttons */}
            <div className="flex items-center justify-between mt-3 gap-1">
              {[0, 25, 50, 75, 100].map((level) => (
                <button
                  key={level}
                  onClick={() => setVolume(level)}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-body font-medium transition-all cursor-pointer whitespace-nowrap ${
                    volume === level
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {level === 0 ? <i className="ri-volume-mute-line"></i> : `${level}`}
                </button>
              ))}
            </div>

            {/* Status */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
              <span className="font-body text-xs text-gray-500">
                {isPlaying ? '🎵 Playing' : '🔇 Paused'}
              </span>
              <button
                onClick={toggleMusic}
                className={`px-3 py-1 rounded-full text-xs font-body font-medium transition-all cursor-pointer whitespace-nowrap ${
                  isPlaying
                    ? 'bg-rose-100 text-rose-600 hover:bg-rose-200'
                    : 'bg-teal-100 text-teal-600 hover:bg-teal-200'
                }`}
              >
                {isPlaying ? 'Stop' : 'Play'}
              </button>
            </div>

            {/* Arrow pointer */}
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-gray-200 transform rotate-45"></div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Button */}
      <button
        onClick={() => setShowSlider(!showSlider)}
        className={`bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 border-2 group cursor-pointer ${
          isPlaying ? 'border-teal-400' : 'border-gray-200 hover:border-teal-400'
        }`}
        aria-label="Music controls"
        title="Click to adjust volume"
      >
        <div className="relative">
          <i className={`${getVolumeIcon()} text-2xl ${isPlaying ? 'text-teal-600' : 'text-gray-400 group-hover:text-teal-600'}`}></i>
          {isPlaying && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          )}
        </div>
      </button>
    </div>
  );
}
